import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Paper, useTheme, IconButton, Skeleton, Chip } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Help as HelpIcon,
} from '@mui/icons-material';

import { useSubmissionsByDealer } from 'services/Data/SubmissionServices';
import { useCurrentQuarter } from 'services/Data/QuarterServices';
import { useCriterias } from 'services/Data/CriteriaServices';
import { useDealer } from 'services/Data/DealerServices';
import CreateSubmissionDialog from '../components/CreateSubmissionDialog';
import LinearProgressLabel from './components/LinearProgressLabel';
import { getHomePageTourSteps } from '../tourSteps/homePageTourSteps';
import GlobalTour from '../tourSteps/GlobalTour';
import CertificationHeroCarousel from './components/CertificationHeroCarousel';
import { CriteriaCertificationTypeEnumValue } from 'enums/CriteriaCertificationTypeEnum';
import {
  getEligibleCriteriasForDealer,
  getFirstCriteriaByType,
  getLatestSubmissionForCriteria,
  getCriteriaCertificationTypeValue,
  normalizeCollection,
} from 'services/Utilities/certificationCriteriaUtils';
import {
  formatCertificationLongDate,
  getCertifiedUntilDate,
  getCompletedSubmissionCount,
  getExteriorStatusChipConfig,
  getExteriorStatusLabel,
  getSubmissionSentOnLabel,
} from 'services/Utilities/certificationSubmissionSummaryUtils';

const HomePage = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const account = useSelector(state => state.AccountReducer);
  const dealerId = account?.profile?.dealer_Id || null;
  const { data: submissions, isLoading: isLoadingSubmissions } = useSubmissionsByDealer(dealerId);
  const { data: criterias, isLoading: isLoadingCriterias } = useCriterias();
  const { data: dealerDetails, isLoading: isLoadingDealerDetails } = useDealer(dealerId, { enabled: !!dealerId });
  const [createDialogState, setCreateDialogState] = useState({
    open: false,
    initialCriteriaId: null,
    criteriaCertificationType: null,
  });
  const currentLanguage = i18n.language;
  const { data: currentQuarter, isLoading: isLoadingCurrentQuarter } = useCurrentQuarter();
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourSteps, setTourSteps] = useState([]);

  useEffect(() => {
    setTourSteps(getHomePageTourSteps(t));
  }, [t]);

  const nextTourStep = () => {
    if (tourStep < tourSteps.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      setShowTour(false);
      setTourStep(0);
    }
  };

  const closeTour = () => {
    setShowTour(false);
    setTourStep(0);
  };

  function startTour() {
    const cleaned = tourSteps.filter(step => document.querySelector(step.target));

    setTourSteps(cleaned);
    setTourStep(0);
    setShowTour(true);
  }

  const openCreateDialog = (options = {}) => {
    setCreateDialogState({
      open: true,
      initialCriteriaId: options.initialCriteriaId ?? null,
      criteriaCertificationType: options.criteriaCertificationType ?? null,
    });
  };

  const closeCreateDialog = () => {
    setCreateDialogState({
      open: false,
      initialCriteriaId: null,
      criteriaCertificationType: null,
    });
  };

  const normalizedSubmissions = useMemo(() => normalizeCollection(submissions), [submissions]);
  const normalizedCriterias = useMemo(() => normalizeCollection(criterias), [criterias]);

  const eligibleCriterias = useMemo(
    () =>
      getEligibleCriteriasForDealer({
        criterias: normalizedCriterias,
        dealerId,
        dealerDetails,
      }),
    [normalizedCriterias, dealerId, dealerDetails],
  );

  const firstEligibleRetailCriteria = useMemo(
    () => getFirstCriteriaByType(eligibleCriterias, CriteriaCertificationTypeEnumValue.RetailEnvironment),
    [eligibleCriterias],
  );
  const firstEligibleExteriorCriteria = useMemo(
    () => getFirstCriteriaByType(eligibleCriterias, CriteriaCertificationTypeEnumValue.ExteriorSignage),
    [eligibleCriterias],
  );

  const criteriaTypeById = useMemo(() => {
    const map = new Map();
    normalizedCriterias.forEach(criteria => {
      if (!criteria?.id) {
        return;
      }

      map.set(criteria.id, getCriteriaCertificationTypeValue(criteria));
    });
    return map;
  }, [normalizedCriterias]);

  const retailSubmissions = useMemo(
    () =>
      normalizedSubmissions.filter(submission => {
        const criteriaTypeFromCriteria = getCriteriaCertificationTypeValue(submission?.criteria);
        const criteriaType = criteriaTypeFromCriteria ?? criteriaTypeById.get(submission?.criteria_Id) ?? null;

        return criteriaType !== CriteriaCertificationTypeEnumValue.ExteriorSignage;
      }),
    [normalizedSubmissions, criteriaTypeById],
  );

  // Calculate stats from submissions
  const stats = useMemo(() => {
    if (!normalizedSubmissions || !Array.isArray(normalizedSubmissions)) {
      return { active: 0, completed: 0 };
    }

    const completed = getCompletedSubmissionCount(normalizedSubmissions);

    return {
      active: normalizedSubmissions.length - completed,
      completed,
    };
  }, [normalizedSubmissions]);

  const certifiedUntilDate = useMemo(() => getCertifiedUntilDate(retailSubmissions), [retailSubmissions]);

  const formattedCertifiedUntil = useMemo(() => formatCertificationLongDate(certifiedUntilDate, currentLanguage), [
    certifiedUntilDate,
    currentLanguage,
  ]);

  const hasExteriorEligibility = Boolean(firstEligibleExteriorCriteria);
  const isLoadingTopCards = isLoadingSubmissions || isLoadingCriterias || isLoadingDealerDetails || isLoadingCurrentQuarter;
  const latestExteriorSubmission = useMemo(
    () => getLatestSubmissionForCriteria(normalizedSubmissions, firstEligibleExteriorCriteria?.id),
    [normalizedSubmissions, firstEligibleExteriorCriteria],
  );

  const exteriorSubmittedOnLabel = useMemo(() => getSubmissionSentOnLabel(latestExteriorSubmission, currentLanguage, t), [
    latestExteriorSubmission,
    currentLanguage,
    t,
  ]);

  const exteriorStatusLabel = useMemo(() => {
    return getExteriorStatusLabel(latestExteriorSubmission, currentLanguage, t);
  }, [latestExteriorSubmission, currentLanguage, t]);

  const exteriorStatusChipConfig = useMemo(() => getExteriorStatusChipConfig(latestExteriorSubmission), [
    latestExteriorSubmission,
  ]);

  const ExteriorStatusIcon = exteriorStatusChipConfig?.icon;

  const carouselSlides = [
    {
      image: '/images/certification/brp2023-07-mta-my24-int-photo-tropical-final.avif',
      category: t('carousel.certification'),
      title: t('carousel.certificationTitle'),
      subtitle: t('carousel.certificationSubtitle'),
    },
    {
      image: '/images/certification/vision.avif',
      category: t('carousel.excellence'),
      title: t('carousel.excellenceTitle'),
      subtitle: t('carousel.excellenceSubtitle'),
    },
    {
      image: '/images/certification/lynx-my22-49r-900ace-snowy-forest-northern-lights-62a1152.avif',
      category: t('carousel.progress'),
      title: t('carousel.progressTitle'),
      subtitle: t('carousel.progressSubtitle'),
    },
    {
      image: '/images/certification/csr-ev-capacity.avif',
      category: t('carousel.innovation'),
      title: t('carousel.innovationTitle'),
      subtitle: t('carousel.innovationSubtitle'),
    },
  ];

  const dashboardCards = [
    {
      key: 'create',
      type: 'action',
      title: t('submissions.createNew'),
      description: t('submissions.selectCriteriaToBegin'),
      icon: <AddIcon sx={{ fontSize: 44 }} />,
      color: '#FFD200',
      action: () => openCreateDialog(),
      tourAttr: 'create-submission',
    },
    {
      key: 'active',
      type: 'stat',
      title: t('home.activeCertifications'),
      description: t('home.activeCertificationsDesc'),
      icon: <CheckCircleIcon sx={{ fontSize: 48 }} />,
      path: '/dealer/certification/submissions',
      color: '#FFD200',
      count: isLoadingSubmissions ? null : stats.active,
      tourAttr: 'active-certifications',
    },
    {
      key: 'completed',
      type: 'stat',
      title: t('home.completed'),
      description: t('home.completedDesc'),
      icon: <HistoryIcon sx={{ fontSize: 48 }} />,
      path: '/dealer/certification/submissions#completed-submissions-list',
      color: '#00bfdf',
      count: isLoadingSubmissions ? null : stats.completed,
      tourAttr: 'completed-certifications',
    },
  ];

  return (
    <>
      <Box>
        <IconButton
          onClick={() => startTour()}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            bgcolor: '#FFD200',
            color: '#000',
            zIndex: 1000,
            '&:hover': {
              bgcolor: '#B8941E',
            },
          }}>
          <HelpIcon />
        </IconButton>

        {/* Hero Carousel - Fullscreen */}
        <CertificationHeroCarousel slides={carouselSlides} />

        <Box
          sx={{
            maxWidth: { xs: '100%', md: 1200, lg: 1400, xl: 1600 },
            mx: 'auto',
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 3 },
          }}>
          {/* Dashboard Content - Centered with Max Width */}
          {!hasExteriorEligibility ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
                mt: 2,
                mb: -2,
              }}>
              {!formattedCertifiedUntil && currentQuarter ? (
                <LinearProgressLabel quarter={currentQuarter} />
              ) : (
                <Typography
                  data-tour="certified-until"
                  variant="h1"
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.text.primary,
                    fontWeight: 900,
                    fontSize: { xs: 42, md: 60 },
                  }}>
                  {t('home.certifiedUntil', {
                    certifiedUntil: formattedCertifiedUntil,
                  })}
                </Typography>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: { xs: 2, md: 3 },
                position: 'relative',
                mt: 2,
                mb: -2,
              }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  p: { xs: 3, md: 4 },
                  border:
                    theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.05)'
                      : `1px solid ${theme.palette.borderColor.main}`,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
                }}>
                <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h2" sx={{ fontWeight: 700, textAlign: 'center' }}>
                    {t('home.retailEnvironment')}
                  </Typography>
                  <IconButton
                    aria-label={t('home.createRetailSubmission')}
                    onClick={() =>
                      openCreateDialog({
                        criteriaCertificationType: CriteriaCertificationTypeEnumValue.RetailEnvironment,
                        initialCriteriaId: firstEligibleRetailCriteria?.id ?? null,
                      })
                    }
                    size="small"
                    sx={{
                      position: 'absolute',
                      right: 0,
                      color: '#000',
                      bgcolor: '#FFD200',
                      '&:hover': { bgcolor: '#EFBA29' },
                    }}>
                    <AddIcon />
                  </IconButton>
                </Box>

                {isLoadingTopCards ? (
                  <Box>
                    <Skeleton variant="text" width="45%" height={28} sx={{ mx: 'auto', mb: 1 }} />
                    <Skeleton variant="rounded" height={20} sx={{ mx: 'auto', width: '92%', borderRadius: 5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, width: '92%', mx: 'auto' }}>
                      <Skeleton variant="text" width={100} />
                      <Skeleton variant="text" width={100} />
                    </Box>
                    <Skeleton variant="text" width="30%" sx={{ mx: 'auto', mt: 1 }} />
                  </Box>
                ) : !formattedCertifiedUntil && currentQuarter ? (
                  <Box
                    sx={{
                      mt: 4,
                      minHeight: { xs: 100, md: 120 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <LinearProgressLabel quarter={currentQuarter} progressContainerWidth="92%" />
                  </Box>
                ) : (
                  <Typography
                    data-tour="certified-until"
                    variant="h1"
                    sx={{
                      textAlign: 'center',
                      color: theme.palette.text.primary,
                      fontWeight: 900,
                      fontSize: { xs: 32, md: 44 },
                    }}>
                    {t('home.certifiedUntil', {
                      certifiedUntil: formattedCertifiedUntil,
                    })}
                  </Typography>
                )}
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  p: { xs: 3, md: 4 },
                  border:
                    theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.05)'
                      : `1px solid ${theme.palette.borderColor.main}`,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
                }}>
                <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h2" sx={{ fontWeight: 700, textAlign: 'center' }}>
                    {t('home.exteriorSignage')}
                  </Typography>
                  <IconButton
                    aria-label={t('home.createExteriorSubmission')}
                    onClick={() =>
                      openCreateDialog({
                        criteriaCertificationType: CriteriaCertificationTypeEnumValue.ExteriorSignage,
                        initialCriteriaId: firstEligibleExteriorCriteria?.id ?? null,
                      })
                    }
                    size="small"
                    sx={{
                      position: 'absolute',
                      right: 0,
                      color: '#000',
                      bgcolor: '#FFD200',
                      '&:hover': { bgcolor: '#EFBA29' },
                    }}>
                    <AddIcon />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    minHeight: { xs: 100, md: 120 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  {isLoadingTopCards ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Skeleton variant="rounded" width={220} height={32} sx={{ borderRadius: 4, mb: 2 }} />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="45%" />
                    </Box>
                  ) : latestExteriorSubmission ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Chip
                        icon={ExteriorStatusIcon ? <ExteriorStatusIcon sx={{ fontSize: 24 }} /> : null}
                        label={exteriorStatusLabel}
                        size="medium"
                        onClick={() =>
                          history.push(`/dealer/certification/submissions/${latestExteriorSubmission.id}/step/1`)
                        }
                        sx={{
                          mt: 2,
                          bgcolor: exteriorStatusChipConfig?.bgColor || 'rgba(153,153,153,0.1)',
                          color: exteriorStatusChipConfig?.color || '#999',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: { xs: 16, md: 18 },
                          p: 2,
                          minHeight: { xs: 48, md: 56 },
                          borderRadius: 6,
                          border: '1px solid',
                          borderColor: exteriorStatusChipConfig?.color || '#999',
                          '&:hover': {
                            boxShadow: `0 6px 14px ${exteriorStatusChipConfig?.color || '#999'}40`,
                          },
                          '& .MuiChip-label': {
                            px: 1.75,
                          },
                          '& .MuiChip-icon': {
                            color: exteriorStatusChipConfig?.color || '#999',
                          },
                        }}
                      />
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ textAlign: 'center', display: 'block' }}>
                          {exteriorSubmittedOnLabel}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        openCreateDialog({
                          criteriaCertificationType: CriteriaCertificationTypeEnumValue.ExteriorSignage,
                          initialCriteriaId: firstEligibleExteriorCriteria?.id ?? null,
                        })
                      }
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openCreateDialog({
                            criteriaCertificationType: CriteriaCertificationTypeEnumValue.ExteriorSignage,
                            initialCriteriaId: firstEligibleExteriorCriteria?.id ?? null,
                          });
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        textAlign: 'center',
                        py: 2,
                        px: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        transition: 'all 0.3s ease',
                        borderRadius: 3,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          '& .exterior-action-icon': {
                            transform: 'scale(1.08)',
                            boxShadow: '0 8px 16px rgba(46, 125, 50, 0.25)',
                          },
                        },
                        outline: 'none',
                      }}>
                      <Box
                        className="exterior-action-icon"
                        sx={{
                          width: 88,
                          height: 88,
                          borderRadius: '50%',
                          bgcolor: 'rgba(46, 125, 50, 0.12)',
                          color: '#2e7d32',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 6px 14px rgba(46, 125, 50, 0.16)',
                          mb: 1,
                        }}>
                        <AddIcon sx={{ fontSize: 44 }} />
                      </Box>

                      <Typography
                        variant="h1"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          fontSize: { xs: 16, md: 18 },
                        }}>
                        {t('home.createExteriorSubmission')}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: { xs: 13, md: 14 },
                          lineHeight: 1.6,
                          maxWidth: 280,
                        }}>
                        {t('home.exteriorCreateCta')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          )}

          <Box
            sx={{
              maxWidth: { xs: '100%', sm: 720, md: 960, lg: 1280, xl: 1920 },
              mx: 'auto',
              pt: { xs: 2, md: 4 },
              pb: { xs: 3, md: 6 },
              px: { xs: 2, sm: 3, md: 4 },
            }}>
            <br />
            {/* Stats Cards */}
            <Paper
              elevation={0}
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
                borderRadius: 4,
                p: { xs: 3, md: 5 },
                border:
                  theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : `1px solid ${theme.palette.borderColor.main}`,
              }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: { xs: 4, md: 6 },
                }}>
                {dashboardCards.map((card, index) => {
                  const isActionCard = card.type === 'action';
                  const showStatSkeleton = card.type === 'stat' && isLoadingSubmissions;

                  return (
                    <Box
                      key={card.key}
                      data-tour={card.tourAttr}
                      onClick={() => {
                        if (isActionCard) {
                          card.action();
                        } else if (card.path) {
                          history.push(card.path);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          if (isActionCard) {
                            card.action();
                          } else if (card.path) {
                            history.push(card.path);
                          }
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        textAlign: 'center',
                        py: 3,
                        px: 2,
                        borderRight:
                          index < dashboardCards.length - 1
                            ? {
                                xs: 'none',
                                sm:
                                  theme.palette.mode === 'dark'
                                    ? '1px solid rgba(255, 255, 255, 0.08)'
                                    : `1px solid ${theme.palette.borderColor.main}`,
                              }
                            : 'none',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          '& .stat-number': {
                            color: '#FFD200',
                            transform: 'scale(1.05)',
                          },
                          '& .action-icon': {
                            transform: 'scale(1.08)',
                            boxShadow: `0 8px 16px ${card.color}40`,
                          },
                        },
                        outline: 'none',
                      }}>
                      {isActionCard ? (
                        <Box
                          className="action-icon"
                          sx={{
                            width: 88,
                            height: 88,
                            borderRadius: '50%',
                            bgcolor: `${card.color}1A`,
                            color: card.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            boxShadow: `0 6px 14px ${card.color}26`,
                          }}>
                          {card.icon}
                        </Box>
                      ) : (
                        <>
                          {showStatSkeleton ? (
                            <Skeleton
                              variant="text"
                              sx={{
                                mb: 1,
                                mx: 'auto',
                                transform: 'none',
                              }}
                              width={72}
                              height={72}
                            />
                          ) : (
                            <Typography
                              className="stat-number"
                              variant="h1"
                              sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 900,
                                fontSize: { xs: 42, md: 60 },
                                mb: 1,
                                transition: 'all 0.3s ease',
                              }}>
                              {card.count}
                            </Typography>
                          )}
                        </>
                      )}

                      <Typography
                        variant="h1"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          fontSize: { xs: 16, md: 18 },
                        }}>
                        {card.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: { xs: 13, md: 14 },
                          lineHeight: 1.6,
                          maxWidth: 240,
                        }}>
                        {card.description}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      <GlobalTour show={showTour} steps={tourSteps} stepIndex={tourStep} onClose={closeTour} onNext={nextTourStep} t={t} />

      <CreateSubmissionDialog
        open={createDialogState.open}
        onClose={closeCreateDialog}
        initialCriteriaId={createDialogState.initialCriteriaId}
        criteriaCertificationType={createDialogState.criteriaCertificationType}
      />
    </>
  );
};

export default HomePage;
